import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trophy, Rocket, Fire, Star, Crown, ArrowsLeftRight, ShareNetwork, Heart, UserPlus, Lock } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Rocket, Fire, Star, Crown, ArrowsLeftRight, ShareNetwork, Heart, UserPlus, Trophy, Lock,
  HeartBreak: Heart,
};

export default function BadgesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBadges = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/badges`, { withCredentials: true });
      setData(d);
    } catch (error) { console.error("Badges fetch error:", error.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin w-6 h-6 border-2 border-[#3F3F46] border-t-[#8B5CF6] rounded-full" />
    </div>
  );

  if (!data) return null;

  const unlockedCount = data.badges.filter(b => b.unlocked).length;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="badges-panel">
      {/* Progress */}
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5 text-center">
        <Trophy size={28} weight="duotone" className="mx-auto text-[#A78BFA] mb-2" />
        <p className="text-2xl font-black text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {unlockedCount} / {data.badges.length}
        </p>
        <p className="text-sm text-[#A1A1AA]">badges débloqués</p>
        <div className="w-full h-2 bg-[#27272A] rounded-full overflow-hidden mt-3 max-w-xs mx-auto">
          <div className="h-full bg-[#8B5CF6] rounded-full transition-all" style={{ width: `${(unlockedCount / data.badges.length) * 100}%` }} />
        </div>
      </div>

      {/* Badge grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {data.badges.map(badge => {
          const Icon = ICON_MAP[badge.icon] || Star;
          return (
            <div key={badge.id}
              className={`border rounded-lg p-4 text-center transition-all ${
                badge.unlocked
                  ? 'bg-[#8B5CF6]/10 border-[#8B5CF6]/40 hover:border-[#8B5CF6]'
                  : 'bg-[#18181B] border-[#27272A] opacity-50'
              }`}
              data-testid={`badge-${badge.id}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
                badge.unlocked ? 'bg-[#8B5CF6]/20' : 'bg-[#27272A]'
              }`}>
                {badge.unlocked ? (
                  <Icon size={20} weight="duotone" className="text-[#A78BFA]" />
                ) : (
                  <Lock size={16} weight="duotone" className="text-[#3F3F46]" />
                )}
              </div>
              <p className={`text-xs font-bold mb-0.5 ${badge.unlocked ? 'text-[#F4F4F5]' : 'text-[#71717A]'}`}>{badge.name}</p>
              <p className="text-[10px] text-[#71717A] leading-tight">{badge.desc}</p>
            </div>
          );
        })}
      </div>

      {/* Stats */}
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4">
        <p className="text-xs font-bold text-[#71717A] mb-2 uppercase tracking-wider">Vos statistiques</p>
        <div className="flex flex-wrap gap-4 text-sm">
          <span className="text-[#E4E4E7]"><strong className="text-[#A78BFA]">{data.stats.posts}</strong> posts</span>
          <span className="text-[#E4E4E7]"><strong className="text-[#A78BFA]">{data.stats.avant_apres}</strong> avant/après</span>
          <span className="text-[#E4E4E7]"><strong className="text-[#A78BFA]">{data.stats.gallery}</strong> en galerie</span>
          <span className="text-[#E4E4E7]"><strong className="text-[#A78BFA]">{data.stats.likes}</strong> likes</span>
          <span className="text-[#E4E4E7]"><strong className="text-[#A78BFA]">{data.stats.referrals}</strong> filleuls</span>
        </div>
      </div>
    </div>
  );
}
